/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { defaultButtonStyles, TabbedButtonList } from "@components/elements/molecules/tabbed-button-list";
import { ResellersSearchSectionFragment } from "@cosmos/hygraph-schema";
import { SearchInput } from "@eds-web/react";
import { useMessages } from "@epic-mw/i18n";
import { css, cx } from "@panda/css";
import { HStack } from "@panda/jsx";
import type { ChangeEvent, KeyboardEvent, MouseEvent } from "react";
import { useCallback, useMemo } from "react";

export type ResellersSearchHeaderProps = Pick<ResellersSearchSectionFragment, "searchLabel"> & {
	searchValue: string;
	currentFilter: string;
	noFilterPath: string;
	searchFilters: { id: string; tabLabel: string }[];
	onValueChange: (value: string, event?: ChangeEvent<HTMLInputElement>) => void;
	onFilterChange: (value: string, event?: KeyboardEvent<HTMLAnchorElement> | MouseEvent<HTMLAnchorElement>) => void;
};

const inputStyles = css({
	maxWidth: "400px",
	mdDown: {
		maxWidth: "full",
	},
	"& input": {
		backdropFilter: "blur(75px)",
	},
});

const buttonStyles = cx(
	defaultButtonStyles,
	css({
		paddingInline: "eds.dimension.20",
		borderWidth: "1px",
		lineHeight: "100%",
		minHeight: "eds.dimension.32",
		"&:not(.tab-button-active)": {
			backgroundColor: "eds.palette.transparent.light.005 !important",
			backdropFilter: "blur(75px) !important",
		},
	}),
);

export const ResellersSearchHeader = ({
	searchLabel,
	searchValue,
	currentFilter,
	onFilterChange,
	onValueChange,
	searchFilters,
}: ResellersSearchHeaderProps): React.JSX.Element => {
	const messages = useMessages();
	const clearSearchLabel = messages["epic.cosmos.search.clear.button"];
	const tabs = useMemo(() => searchFilters || [], [searchFilters]);
	const defaultIndex = useMemo(() => tabs.findIndex((tab) => currentFilter.includes(tab.id)), [currentFilter, tabs]);

	const handleButtonClick = useCallback(
		(index: number) => {
			const tabId = tabs[index]?.id;
			if (tabId) {
				if (currentFilter.includes(tabId)) return onFilterChange("");
				onFilterChange(tabId);
			}
		},
		[tabs, currentFilter, onFilterChange],
	);

	return (
		<HStack
			gap="eds.dimension.24"
			justifyContent="space-between"
			alignItems="center"
			width="full"
			mdDown={{ flexWrap: "wrap" }}
		>
			<TabbedButtonList
				tabs={tabs}
				onTabChange={handleButtonClick}
				buttonStyles={buttonStyles}
				defaultIndex={defaultIndex}
				tabToggle={true}
				secondaryButtonVariant="outline"
				activeButtonVariant="contrast"
			/>
			<SearchInput
				size="md"
				label={searchLabel || "Search"}
				clearLabel={clearSearchLabel || "Clear search"}
				className={inputStyles}
				value={searchValue}
				onChange={onValueChange}
			/>
		</HStack>
	);
};
